import React from "react"
import axios from "axios"
import clsx from "clsx"
import { toast } from "react-toastify"
import MySelectCreatableFormField from "@common-components/fields/MySelectCreatableFormField"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import MyDateMonthYearFormField from "@common-components/fields/MyDateMonthYearFormField"
import { CONST_MONTANT_INDEMNITE_PAR_KM, CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM } from "@common-constants/*"
import { CONST_API_UTILS_GET_GOOGLE_MAPS_ADDRESS_AUTOCOMPLETION_ENDPOINT, CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT, } from "../../../../../../../../../constants"

export const NdfMileageSection = ({ helpers, validationSchema, detailsIndemniteKm, setDetailsIndemniteKm }: any) => {
     if (helpers.values.categorie !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM) return null

     async function calculateMontantIndemniteKmEntreDepartEtDestination(departure?: string, arrival?: string) {
          if (departure && arrival) {
               return axios
                    .post<{ distance: number | null }>(CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT, { departure, arrival })
                    .then(({ data: { distance } }) => {
                         if (distance) setDetailsIndemniteKm((prev: any) => ({ ...prev, distanceEnKmEntreDepartEtDestination: distance }))
                         return distance || 0
                    })
          }
          return 0
     }

     function calculateMontantIndemniteKmTotal(distance: number, isAllerRetour: boolean, nbTrajets: number = 0) {
          return distance > 0 ? Number((distance * (isAllerRetour ? nbTrajets * 2 : nbTrajets)).toFixed(2)) : 0
     }

     const syncKM = (dist: number, ar: boolean, nb: number) => {
          const dt = calculateMontantIndemniteKmTotal(dist, ar, nb)
          setDetailsIndemniteKm((prev: any) => ({
               ...prev,
               distanceTotal: dt,
               montantTTC: Number((dt * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)),
          }))
          if (nb) helpers.setFieldValue("distanceTotale", dt)
     }

     // ---- VARIABLES ET CONDITIONS D'AFFICHAGE ----
     const moisAnnee = helpers.values.moisAnneeKm
     const joursKm = helpers.values.joursKm || []
     const maxTrajets = helpers.values.nbTrajets || 0

     // On ne débloque l'agenda et les détails de distance que si la section précédente est remplie
     const isAgendaReady = moisAnnee && helpers.values.adresseDepart && helpers.values.adresseArrive && maxTrajets > 0

     const renderAgenda = () => {
          if (!moisAnnee || !maxTrajets) return null

          const [year, month] = moisAnnee.split("-").map(Number)
          const daysInMonth = new Date(year, month, 0).getDate()

          // Formatage de la date en "Mois Année" (ex: "Avril 2026")
          const monthNameRaw = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(new Date(year, month - 1))
          const formattedMonth = monthNameRaw.charAt(0).toUpperCase() + monthNameRaw.slice(1)

          // Tableau d'objets contenant le jour et s'il s'agit d'un week-end
          const daysArray = Array.from({ length: daysInMonth }, (_, i) => {
               const dayNumber = i + 1
               const dateObj = new Date(year, month - 1, dayNumber)
               const dayOfWeek = dateObj.getDay() // 0 = Dimanche, 6 = Samedi
               const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

               // Obtenir la première lettre du jour en français (L, M, M, J, V, S, D)
               const letter = new Intl.DateTimeFormat("fr-FR", { weekday: "narrow" }).format(dateObj)

               return { day: dayNumber, isWeekend, letter }
          })

          const isExactMatch = joursKm.length === maxTrajets

          const handleDayClick = (day: number) => {
               if (joursKm.includes(day)) {
                    helpers.setFieldValue(
                         "joursKm",
                         joursKm.filter((d: number) => d !== day)
                    )
               } else {
                    if (joursKm.length >= maxTrajets) {
                         toast.warning(`Vous devez sélectionner exactement ${maxTrajets} jour(s). Désélectionnez-en un d'abord ou augmentez le nombre de trajets.`)
                         return
                    }
                    helpers.setFieldValue(
                         "joursKm",
                         [...joursKm, day].sort((a, b) => a - b)
                    )
               }
          }

          return (
               <div className="row mb-6 animate__animated animate__fadeIn">
                    <div className="col-lg-4"></div>
                    <div className="col-lg-8">
                         {/* 1. On détecte s'il y a une erreur sur ce champ */}
                         <div
                              className={clsx(
                                   "p-5 border border-dashed rounded transition-all",
                                   helpers.errors.joursKm
                                        ? "border-danger bg-light-danger bg-opacity-25" // Bordure et fond rouge si erreur
                                        : isExactMatch
                                        ? "border-success bg-light-success bg-opacity-25"
                                        : "border-primary bg-light-primary bg-opacity-25"
                              )}
                         >
                              {/* AFFICHAGE DU MOIS CONCERNÉ ICI */}
                              <h6 className={clsx("mb-4 fw-boldest", helpers.errors.joursKm ? "text-danger" : isExactMatch ? "text-success" : "text-primary")}>
                                   Sélectionnez exactement {maxTrajets} jour(s) en {formattedMonth}
                              </h6>

                              <div className="d-flex flex-wrap gap-2">
                                   {daysArray.map(({ day, isWeekend, letter }) => {
                                        const isSelected = joursKm.includes(day)
                                        return (
                                             <button
                                                  key={day}
                                                  type="button"
                                                  onClick={() => handleDayClick(day)}
                                                  className={clsx(
                                                       "btn btn-icon d-flex flex-column justify-content-center align-items-center fw-bold transition-all",
                                                       isSelected
                                                            ? "btn-primary shadow-sm"
                                                            : isWeekend
                                                            ? // 2. Remplacement du rouge par un gris clair pour le week-end
                                                              "bg-light text-muted border border-gray-300 opacity-75 hover-elevate-up"
                                                            : "bg-white text-gray-700 border border-gray-300 hover-elevate-up"
                                                  )}
                                                  style={{ width: "42px", height: "48px" }}
                                             >
                                                  <span className={clsx("fs-9", isSelected ? "text-white opacity-75" : "text-muted opacity-75")}>{letter}</span>
                                                  <span className="fs-6 lh-1 mt-1">{day}</span>
                                             </button>
                                        )
                                   })}
                              </div>

                              <div className="mt-4 fs-7 d-flex align-items-center">
                                   <i className={clsx("fas fa-info-circle me-2", helpers.errors.joursKm ? "text-danger" : isExactMatch ? "text-success" : "text-muted")}></i>
                                   <span className="text-muted">Jours sélectionnés :</span>
                                   <strong className={clsx("ms-1", isExactMatch ? "text-success" : "text-danger")}>{joursKm.length}</strong>
                                   <span className="text-muted"> / {maxTrajets}</span>
                              </div>

                              {helpers.errors.joursKm && <div className="text-danger mt-2 fs-7 fw-bold">{helpers.errors.joursKm}</div>}
                         </div>
                    </div>
               </div>
          )
     }

     return (
          <>
               {/* ÉTAPE 1 : SELECTION DU MOIS - Toujours visible en premier */}
               <div className="mb-6">
                    <MyDateMonthYearFormField
                         name="moisAnneeKm"
                         formikProps={helpers}
                         validationSchema={validationSchema}
                         placeholder="MM/YYYY"
                         label={<div className="required">Choisissez le mois</div>}
                         classNameLabel="col-lg-4"
                         classNameField="col-lg-8"
                         onChange={() => helpers.setFieldValue("joursKm", [])}
                    />
               </div>

               {/* ÉTAPE 2 : ADRESSES ET TRAJETS - N'apparaît que si le mois est sélectionné */}
               {moisAnnee && (
                    <div className="animate__animated animate__fadeIn">
                         <MySelectCreatableFormField
                              label={<div className="required">Adresse de départ</div>}
                              name="adresseDepart"
                              endpoint={CONST_API_UTILS_GET_GOOGLE_MAPS_ADDRESS_AUTOCOMPLETION_ENDPOINT}
                              method="GET"
                              minimumLengthSearch={8}
                              placeholder="Saisissez une adresse"
                              classNameLabel="col-lg-4"
                              classNameField="col-lg-8"
                              classNameLayout={"mb-2"}
                              isMulti={false}
                              formikProps={helpers}
                              validationSchema={validationSchema}
                              onChangeCallback={val => {
                                   calculateMontantIndemniteKmEntreDepartEtDestination(val as string, helpers.values.adresseArrive).then(dist =>
                                        syncKM(dist, !!helpers.values.isAllerRetour, helpers.values.nbTrajets)
                                   )
                              }}
                         />
                         <MySelectCreatableFormField
                              label={<div className="required">Adresse de destination</div>}
                              name="adresseArrive"
                              endpoint={CONST_API_UTILS_GET_GOOGLE_MAPS_ADDRESS_AUTOCOMPLETION_ENDPOINT}
                              method="GET"
                              minimumLengthSearch={8}
                              placeholder="Saisissez une adresse"
                              classNameLabel="col-lg-4"
                              classNameField="col-lg-8"
                              classNameLayout={"mb-3"}
                              isMulti={false}
                              formikProps={helpers}
                              validationSchema={validationSchema}
                              onChangeCallback={val => {
                                   calculateMontantIndemniteKmEntreDepartEtDestination(helpers.values.adresseDepart, val as string).then(dist =>
                                        syncKM(dist, !!helpers.values.isAllerRetour, helpers.values.nbTrajets)
                                   )
                              }}
                         />

                         <div className="row mb-2">
                              <label className="col-lg-4 col-form-label fs-6 fw-bolder text-gray-900 required">Nombre de trajets</label>
                              <div className={"col-lg-8 d-flex gap-2"}>
                                   <MyInputFormField
                                        formikProps={helpers}
                                        validationSchema={validationSchema}
                                        name={"nbTrajets"}
                                        type={"number"}
                                        placeholder={"Nombre de trajets"}
                                        classNameLayout={"me-2 mb-0"}
                                        onChangeCallback={val => {
                                             const dt = calculateMontantIndemniteKmTotal(
                                                  detailsIndemniteKm.distanceEnKmEntreDepartEtDestination,
                                                  !!helpers.values.isAllerRetour,
                                                  val
                                             )
                                             setDetailsIndemniteKm((p: any) => ({ ...p, distanceTotal: dt, montantTTC: Number((dt * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)) }))
                                             helpers.setFieldValue("distanceTotale", dt)

                                             // On vide les jours sélectionnés si l'utilisateur réduit le nombre de trajets
                                             if (val < helpers.values.joursKm.length) {
                                                  helpers.setFieldValue("joursKm", [])
                                             }
                                        }}
                                   />
                                   <div className={"d-flex align-items-center"}>
                                        <MyCheckboxFormField
                                             formikProps={helpers}
                                             validationSchema={validationSchema}
                                             name={"isAllerRetour"}
                                             onChangeCallback={val => {
                                                  const dt = calculateMontantIndemniteKmTotal(
                                                       detailsIndemniteKm.distanceEnKmEntreDepartEtDestination,
                                                       val,
                                                       helpers.values.nbTrajets
                                                  )
                                                  setDetailsIndemniteKm((p: any) => ({
                                                       ...p,
                                                       distanceTotal: dt,
                                                       montantTTC: Number((dt * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)),
                                                  }))
                                                  helpers.setFieldValue("distanceTotale", dt)
                                             }}
                                        />
                                        <div style={{ marginLeft: "-1rem" }}>Aller-retour</div>
                                   </div>
                              </div>
                         </div>

                         <MyInputFormField
                              formikProps={helpers}
                              validationSchema={validationSchema}
                              name={"distanceTotale"}
                              type={"number"}
                              placeholder={"Distance totale"}
                              label={<div className={"required"}>Distance totale</div>}
                              classNameLabel={"col-lg-4"}
                              classNameField={"col-lg-8"}
                              classNameLayout={"mb-6"}
                              onChangeCallback={val => {
                                   setDetailsIndemniteKm((p: any) => ({ ...p, distanceTotal: val, montantTTC: Number((val * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)) }))
                              }}
                         />
                    </div>
               )}

               {/* ÉTAPE 3 : L'AGENDA ET LES DETAILS FINAUX - N'apparaît que si Adresses et nbTrajets sont remplis */}
               {isAgendaReady && (
                    <div className="animate__animated animate__fadeIn">
                         {renderAgenda()}

                         <div className={"p-4 border border-dashed text-gray-600 mb-6"}>
                              <h4 className={"mb-4 text-gray-600"}>
                                   Détails de l'indemnité kilométrique
                                   <i className="fas fa-info-circle fs-3 float-end" />
                              </h4>
                              <div className="separator separator-dashed mb-6" />
                              <p>Montant TTC: {detailsIndemniteKm.montantTTC.toFixed(2)}€</p>
                         </div>
                    </div>
               )}
          </>
     )
}
